import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { ArrowRight, Check, PhoneCall, FileText } from "lucide-react";
import { SERVICES, getService } from "@/data/services";
import { FadeIn } from "@/components/Reveal";
import { FinalCTA } from "@/components/FinalCTA";
import { SITE } from "@/config/site";

export default function ServicePage() {
  const { slug } = useParams();
  const service = getService(slug);

  useEffect(() => {
    if (!service) return;
    document.title = `${service.title} | TSM Group CLE`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", `${service.short} TSM Group CLE serves businesses throughout Northeast Ohio.`);
  }, [service]);

  if (!service) {
    return (
      <div className="bg-paper pb-24 pt-32 text-center" data-testid="service-not-found">
        <p className="eyebrow justify-center">Not Found</p>
        <h1 className="mt-6 font-serif text-4xl font-semibold text-navy sm:text-5xl">
          That service page doesn&rsquo;t exist.
        </h1>
        <a href="/" className="btn-outline mt-9 inline-flex">
          Back to Home
        </a>
      </div>
    );
  }

  const Icon = service.icon;

  return (
    <div className="bg-paper" data-testid="service-page">
      <section className="pb-16 pt-14 lg:pb-24 lg:pt-20">
        <div className="container-tsm max-w-[1140px]">
          <a
            href="/#services"
            data-testid="service-page-back"
            className="inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-golddark transition-colors hover:text-navy"
          >
            All Services
            <ArrowRight className="h-3.5 w-3.5 rotate-180" />
          </a>

          <FadeIn>
            <div className="mt-10 flex items-start gap-5">
              <span className="mt-1 flex h-14 w-14 shrink-0 items-center justify-center rounded-sm bg-navy text-gold">
                <Icon className="h-6 w-6" />
              </span>
              <h1
                className="font-serif text-4xl font-semibold leading-[1.06] tracking-tight text-navy sm:text-5xl lg:text-6xl"
                data-testid="service-page-title"
              >
                {service.title}
              </h1>
            </div>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-ink/70" data-testid="service-page-description">
              {service.short}
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <a href="/#request" data-testid="service-page-cta-request" className="btn-gold">
                Request this Service
                <ArrowRight className="h-4 w-4" />
              </a>
              <a href={SITE.phoneHref} data-testid="service-page-cta-call" className="btn-outline">
                <PhoneCall className="h-4 w-4" />
                Call TSM Group CLE
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="container-tsm grid gap-14 lg:grid-cols-12">
          <FadeIn className="lg:col-span-7">
            <h2 className="font-serif text-3xl font-semibold text-navy lg:text-4xl">What this service includes</h2>
            <ul className="mt-8 space-y-4" data-testid="service-page-items">
              {service.items.map((item) => (
                <li key={item} className="flex items-start gap-3 border-b border-navy/5 pb-4 text-lg text-ink/80">
                  <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-gold/15">
                    <Check className="h-3.5 w-3.5 text-golddark" strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </FadeIn>

          <div className="space-y-8 lg:col-span-4 lg:col-start-9">
            {service.notes.map((note) => (
              <FadeIn key={note} delay={0.1}>
                <p className="border-l-2 border-gold bg-paper p-5 text-sm italic leading-relaxed text-ink/60">
                  {note}
                </p>
              </FadeIn>
            ))}
            <FadeIn delay={0.15}>
              <div className="rounded-lg border border-navy/10 bg-paper p-6">
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-golddark">
                  Service Area
                </p>
                <p className="mt-3 font-serif text-2xl font-semibold text-navy">{SITE.serviceArea}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink/60">
                  Mobile services may be available depending on service type and location.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <a
                href={SITE.schedulingUrl}
                data-testid="service-page-schedule"
                className="flex items-start gap-3 rounded-lg border border-navy/10 bg-paper p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-gold"
              >
                <FileText className="mt-0.5 h-5 w-5 shrink-0 text-golddark" />
                <span className="text-sm leading-relaxed text-ink/70">
                  <span className="font-semibold text-navy">Schedule a consultation</span> — for business compliance
                  and operational discussions.
                </span>
              </a>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="container-tsm max-w-[1140px]">
          <FadeIn>
            <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-navy/50">
              Explore other services
            </h2>
            <div className="mt-6 flex flex-wrap gap-3" data-testid="service-page-related">
              {SERVICES.filter((s) => s.slug !== service.slug).map((s) => (
                <a
                  key={s.slug}
                  href={`/services/${s.slug}`}
                  className="rounded-full border border-navy/15 bg-white px-5 py-2.5 text-sm font-semibold text-navy/75 transition-all duration-300 hover:border-gold hover:text-navy"
                  data-testid={`service-page-related-${s.slug}`}
                >
                  {s.title}
                </a>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
